import React, { useEffect, useState } from "react";
import { TextField, styled } from "@mui/material";
import { passwordValidationRules } from "../cognito";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";

interface PasswordInputProps {
  label?: string;
  onChange: (value: string) => void;
  onInvalid: (value: boolean) => void;
  withConfirmation?: boolean;
  disabled?: boolean;
  showValidationRules?: boolean;
}

const ValidationRules = styled("div")({
  paddingLeft: 10,
  display: "flex",
  flexDirection: "column",
  gap: 5,
  fontSize: "0.88em",
  color: "silver",
  "& svg": {
    width: "0.68em",
    height: "0.68em",
  },
  "& .validation-rule": {
    display: "flex",
    gap: 5,
  },
  "& .validation-rule.valid": {
    color: "green",
  },
  "& .validation-rule.invalid": {
    color: "red",
  },
});

const PasswordInput = ({
  label = "Password",
  onChange,
  onInvalid,
  withConfirmation = true,
  disabled,
  showValidationRules = true,
}: PasswordInputProps) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationRules, setValidationRules] =
    useState<{ valid: boolean; message: string }[]>(null);
  const [invalid, setInValid] = useState(true);

  useEffect(() => {
    const validation = passwordValidationRules.map((rule) => ({
      valid: password ? rule.validate(password) : undefined,
      message: rule.validationMessage,
    }));
    setValidationRules(validation);
    setInValid(
      validation.some((rule) => rule.valid === false) ||
        (withConfirmation && password !== confirmPassword),
    );
  }, [password, confirmPassword, withConfirmation]);

  useEffect(() => {
    onInvalid(invalid);
  }, [invalid, onInvalid]);

  const handlePasswordChanged = (e) => {
    setPassword(e.target.value);
    onChange(e.target.value);
  };
  const handleConfirmedPasswordChanged = (e) =>
    setConfirmPassword(e.target.value);

  return (
    <>
      <TextField
        className={"password-input"}
        fullWidth
        required
        margin="normal"
        variant="outlined"
        label={label}
        type="password"
        disabled={disabled}
        onChange={handlePasswordChanged}
        error={
          validationRules &&
          validationRules.some((rule) => rule.valid === false)
        }
      />
      {showValidationRules && (
        <ValidationRules className={"validation-rules"}>
          {validationRules &&
            validationRules.map((rule, index) => (
              <div
                key={index}
                className={`validation-rule${
                  rule.valid ? " valid" : rule.valid === false ? " invalid" : ""
                }`}
              >
                {rule.valid && <CheckIcon />}
                {rule.valid === false && <ClearIcon />}
                {rule.message}
              </div>
            ))}
        </ValidationRules>
      )}
      {withConfirmation && (
        <TextField
          className={"password-input repeat"}
          fullWidth
          required
          margin="normal"
          variant="outlined"
          error={password !== confirmPassword}
          title={password !== confirmPassword ? "Passwords do not match" : ""}
          label="Confirm Password"
          type="password"
          disabled={disabled}
          onChange={handleConfirmedPasswordChanged}
        />
      )}
    </>
  );
};

export default PasswordInput;
